import { computed } from 'mobx'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import type {
  BulkCreateCustomersFn,
  CreateCustomerFn,
  CreateMemberManuallyFn,
  GetCurrentMemberFn,
  SearchMembersFn,
  SearchUsersFn,
} from '@/features/iam'
import { ColumnLayout } from '@/infra/ColumnLayout'
import { FilterStorage } from '@/infra/FiltersViewModel'
import { links } from '@/utils/link'
import { type Storage } from '@/infra/storage'
import { Id } from '@/infra/id'
import { ClientUserSearchViewModel } from '@/features/client-user-management/ClientUserSearchViewModel'
import { ClientInvitationDialogViewModel } from '@/features/client-user-management/ClientInvitationDialog/ClientInvitationDialogViewModel'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type ActivationLifecycle, activeWhen } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

export interface ClientUserColumn {
  id: string
  header: string
  modifyVisibility: boolean
  sortable: boolean
  visible: boolean
}

export const clientUserManagementColumns: ClientUserColumn[] = [
  {
    id: 'name',
    header: 'Name',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'email',
    header: 'Email',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'lastLogin',
    header: 'Last Login',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'dateCreated',
    header: 'Date Created',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
]

export class ClientUserManagementViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly columns = clientUserManagementColumns

  readonly columnLayout

  readonly userSearch

  readonly clientInvitationVm

  constructor(
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _localStorage: Storage,
    searchUsers: SearchUsersFn,
    searchMembersByPermission: SearchMembersFn,
    createMemberManually: CreateMemberManuallyFn,
    createCustomer: CreateCustomerFn,
    bulkCreateCustomers: BulkCreateCustomersFn,
    private readonly _newAlert: AlertFactory,
    createAlertErrorHandler: AlertErrorHandlerFactory,
    createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.columnLayout = new ColumnLayout(
      this._localStorage,
      'ClientList Settings',
    )

    this.userSearch = new ClientUserSearchViewModel(
      searchUsers,
      this._getCurrentMember,
      (ns) => new FilterStorage(this._localStorage, ns),
      createAlertRetryWrapper,
      { filterStorageNamespace: 'ClientUserManagementTableFilters' },
    )

    this.clientInvitationVm = new ClientInvitationDialogViewModel(
      this._getCurrentMember,
      searchMembersByPermission,
      createMemberManually,
      createCustomer,
      bulkCreateCustomers,
      this._newAlert,
      createAlertErrorHandler,
    )

    this._lifecycle
      .manage(
        activeWhen(() => this.canAccessClientUserManagement, this.userSearch),
      )
      .manage(this.clientInvitationVm)
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  @computed
  get canAccessClientUserManagement() {
    return this._getCurrentMember().canListClients
  }

  @computed
  get canInviteClients() {
    const currentMember = this._getCurrentMember()
    const hasAdminUserCreate = currentMember.permissions.adminUserCreate

    if (hasAdminUserCreate) {
      return true
    }

    // Customers sign in with a magic link, which needs the workspace's
    // external customer support.
    const hasCustomerCreate = currentMember.permissions.customerCreate
    const workspace = currentMember.workspace
    const externalCustomerConfig = workspace.config.externalCustomer
    const workspaceSupportsCustomers =
      externalCustomerConfig.enabled &&
      externalCustomerConfig.allowRequestingMagicLinks

    return hasCustomerCreate && workspaceSupportsCustomers
  }

  getUserDetailsLinkForUser(user: SearchableUser) {
    return links.clientUserDetails(Id.assert(user.publicId))
  }
}
